/**
 * This file returns typed functions used to report events to Segment.
 * Each event takes a unique payload, which is inferred by the provided
 * SegmentEvent value.
 *
 * How to add new Segment events: https://leaflink.atlassian.net/wiki/x/ZQBo3
 */

import { AnalyticsBrowser } from '@segment/analytics-next';
import { format, parseISO } from 'date-fns';
import merge from 'lodash-es/merge';
import snakeCase from 'lodash-es/snakeCase';
import { storeToRefs } from 'pinia';

import { env } from '@/constants/env';
import { SegmentDefaultProperties, SegmentPayloadMap, SegmentPropertiesMap } from '@/models/segment';

import { DATE_FORMATS } from './constants';
import { SEGMENT_EVENTS, SegmentEvent } from './constants/segment';
import { useAppConfigStore } from './piniaStores/appConfig/appConfig.store';
import { useAuthStore } from './piniaStores/auth/auth.store';
import { useCompanyStore } from './piniaStores/company/company.store';
import { useUserStore } from './piniaStores/user/user.store';

let _analytics: AnalyticsBrowser | null = null;

export function getSegmentAnalytics() {
  return _analytics;
}

// Useful for testing
export function clearSegmentAnalytics() {
  _analytics = null;
}

/**
 * Loads the Segment analytics library, which allows event tracking.
 *
 * *Note:* public (unauthenticated) pages, such as /menu/:brandSlug and /register/retailer/ can track events even though no user is authenticated. After the user logs in, the `identifySegmentUser` function associates the user with the events.
 */
export function loadSegmentAnalytics() {
  if (env.VITE_SEGMENT_ENABLED !== 'true' || !env.VITE_SEGMENT_WRITE_KEY || window.Cypress) {
    return;
  }

  const appConfigStore = useAppConfigStore();

  if (appConfigStore.hasSegmentLoaded) {
    return;
  }

  // TODO: remove this condition after the SSR_IFRAME split is live for all users
  if (typeof window.analytics?.track === 'function') {
    _analytics = window.analytics;
  } else {
    const userStore = useUserStore();
    const { getIsSplitEnabled } = storeToRefs(userStore);

    // User data is available when loadSegmentAnalytics is called within fetchCommonData for authenticated users
    if (userStore.user) {
      const isLeafLinkStaff = userStore.user && (userStore.user.isLeaflinkStaff || userStore.user.isSuperuser);

      // In production, we should only track real user events, not those from hijacked users or internal LeafLink employees.
      // This can be overridden by the force_segment_reporting split flag.
      if (
        env.MODE === 'production' &&
        (userStore.user?.isHijacked || isLeafLinkStaff) &&
        !getIsSplitEnabled.value('force_segment_reporting')
      ) {
        return;
      }
    }

    // API Docs: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/custom-proxy/#npm-instructions
    _analytics = AnalyticsBrowser.load(
      {
        writeKey: env.VITE_SEGMENT_WRITE_KEY,
        cdnURL: 'https://' + env.VITE_SEGMENT_CDN_PROXY_HOST,
      },
      {
        integrations: {
          'Segment.io': {
            apiHost: `${env.VITE_SEGMENT_API_PROXY_HOST}/v1`,
            protocol: 'https',
          },
        },
      },
    );

    // Django pages within the iframe use this via `window.parent._segmentAnalytics`
    window._segmentAnalytics = _analytics;
  }

  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', setupSegmentDataTrack);
  } else {
    setupSegmentDataTrack();
  }

  appConfigStore.hasSegmentLoaded = true;
}

/**
 * Segment does not recommend identifying anonymous users; instead, only call
 * `identify` when we have an authenticated user.
 *
 * @see: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
 */
export function identifySegmentUser() {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();
  const userStore = useUserStore();

  const analytics = getSegmentAnalytics();

  if (!userStore.user.id || !userStore.userActiveCompany || !companyStore.activeCompany || !analytics) {
    return;
  }

  // These keys need a different conversion than the snakeCase util returns
  const permissionsCaseMap = {
    canAccessS2S: 'can_access_s2s',
  };

  analytics.identify(authStore.userId, {
    ...getDefaultSegmentProperties(),
    email: userStore.user.email,
    enabled_splits: Object.keys(userStore.getEnabledSplits()).join(','),
    enable_brand_fulfillment: !!userStore.userActiveCompany.enableBrandFulfillment,
    enable_replenishment: !!companyStore.activeCompany.group?.enableReplenishment,
    splits: userStore.user.splits,
    last_login: userStore.user.lastLogin
      ? format(parseISO(userStore.user.lastLogin), DATE_FORMATS.YYYY_MM_DD_DATE_FNS)
      : undefined,
    live_date: companyStore.activeCompany.liveDate,
    live_date_iso: companyStore.activeCompany.liveDateIso,
    name: userStore.user.firstName.trim() + ' ' + userStore.user.lastName.trim(),
    permissions: Object.entries(userStore.userActiveCompany.staffPermissions)
      .filter(([_key, value]) => !!value)
      .map(([key]) => permissionsCaseMap[key] || snakeCase(key)),
    sign_up_date: format(parseISO(userStore.user.dateJoined), DATE_FORMATS.YYYY_MM_DD_DATE_FNS),
  });
}

/**
 * This reports split experiments and their allocations to Segment.  Experiments are received
 * as a string array on the active_experiments split flag, and we then reach out to Split to
 * get the current value of the experiment's treatment.  The experiments can be arbitary and
 * may not align with our strongly-typed splits that are used day-to-day, so the experiment
 * splits are not stored on the user state.
 */
export async function reportSegmentExperimentAllocations() {
  const userStore = useUserStore();
  const { user } = storeToRefs(userStore);

  if (user.value.splits?.active_experiments && user.value.splits.active_experiments !== 'off') {
    // treatment will be in the shape of ["on", "<stringified_json>"]
    const stringifiedExperiments =
      Array.isArray(user.value.splits.active_experiments) && user.value.splits.active_experiments[0] === 'on'
        ? user.value.splits.active_experiments[1]
        : '{"active_experiments":[]}';

    const activeExperiments: string[] = JSON.parse(stringifiedExperiments).active_experiments;

    if (!activeExperiments.length) {
      return;
    }

    const result: { [key: string]: string } = await userStore.fetchSplitExperiments(activeExperiments.join(','));

    Object.entries(result).forEach(([experiment_name, experiment_allocation]) => {
      trackWithSegment({
        event: 'experimentAllocation',
        properties: {
          experiment_allocation,
          experiment_name,
        },
      });
    });
  }
}

/**
 * This allows the `data-track` attribute to be used on any element to track events with segment.
 * @deprecated Use the `trackWithSegment` function instead.
 *
 * TODO: Delete this function after all `data-track` attributes in marketplace-web and leaflink have been replaced.
 */
export function setupSegmentDataTrack() {
  const body = document.body;

  new MutationObserver((mutations) => {
    mutations.map((m) => {
      (m.addedNodes || []).forEach(_addDataTrackNode);
    });
  }).observe(body, {
    childList: true,
    subtree: true,
  });

  _addDataTrackNode(body);
}

/**
 * Adds data tracking to all DOM nodes with data-track attributes.
 * @param {Element} context The node within which to search for items to track.
 */
function _addDataTrackNode(context) {
  if (context.nodeType === 1) {
    const trackers = context.querySelectorAll(`[data-track]`);

    trackers.forEach((t) =>
      t.addEventListener('click', (e) => {
        const data = _getDataTrackValue(e.currentTarget);

        data && track(data.event)(data.props);
      }),
    );
  }
}

/**
 * Fetches the data from an element's data-track attribute, and formats it
 * for use with the track method.
 * @param {HTMLElement} el The element to fetch data from.
 * @return {object} A tracking object, if successful.
 */
function _getDataTrackValue(el) {
  let data = el.getAttribute('data-track');
  let key: string;

  if (data) {
    if (/^(\s*)(?:\{[\w\W]*\}|\[[\w\W]*\])(\s*)$/.test(data)) {
      data = JSON.parse(data);
      key = Object.keys(data)[0];

      return {
        event: key,
        props: data[key],
      };
    }

    return {
      event: data,
    };
  }
}

/**
 * A wrapper function around Segment Analytic's `track` method with some properties pre-populated.
 */
export function trackWithSegment<TEvent extends keyof SegmentPropertiesMap>(args: {
  event: TEvent;
  properties?: SegmentPropertiesMap[TEvent];
  config?: {
    excludeDefaultProperties?: boolean;
  };
  /** Passed through to the `options` argument for the `track` method */
  options?: Parameters<AnalyticsBrowser['track']>[2];
}) {
  const { event, properties, config = {}, options } = args;

  const analytics = getSegmentAnalytics();
  if (!analytics) {
    return Promise.resolve(false);
  }

  const finalProperties = merge(
    {},
    config.excludeDefaultProperties ? undefined : getDefaultSegmentProperties(),
    properties,
  );

  if (options) {
    return analytics.track(SEGMENT_EVENTS[event], finalProperties, options);
  } else {
    return analytics.track(SEGMENT_EVENTS[event], finalProperties);
  }
}

export function getDefaultSegmentProperties(): SegmentDefaultProperties {
  const authStore = useAuthStore();
  const { user, userActiveCompany } = storeToRefs(useUserStore());
  const { activeCompany } = storeToRefs(useCompanyStore());

  const firstLicense = userActiveCompany.value?.licenses[0];
  const firstLicenseTypeObject = typeof firstLicense?.type === 'object' ? firstLicense?.type : undefined;

  const properties: SegmentDefaultProperties = {
    auth0_id: authStore.userId,
    buyer_id: user.value.isBuyer ? userActiveCompany.value?.id.toString() : undefined,
    buyer_license: user.value.isBuyer ? firstLicense?.number : undefined,
    buyer_license_type: user.value.isBuyer ? firstLicenseTypeObject?.displayType : undefined,
    buyer_license_type_state: user.value.isBuyer ? firstLicenseTypeObject?.state : undefined,
    company_id: activeCompany.value?.id.toString(),
    company_is_on_payments: Boolean(
      activeCompany.value?.enablePaymentsDirect || activeCompany.value?.enablePaymentsFlexible,
    ),
    company_name: activeCompany.value?.name,
    company_slug: userActiveCompany.value?.slug,
    hashed_active_company: user.value.hashedActiveCompany,
    is_admin: Boolean(userActiveCompany.value?.staffIsAdmin),
    is_buyer: Boolean(user.value.isBuyer),
    is_hijacked: Boolean(user.value.isHijacked),
    is_logistics_customer: Boolean(activeCompany.value?.isLogisticsVisible),
    is_seller: Boolean(user.value.isSeller),
    marketplace_user_id: user.value.id ? user.value.id.toString() : undefined,
    seller_id: user.value.isSeller ? userActiveCompany.value?.id.toString() : undefined,
    seller_license: user.value.isSeller ? firstLicense?.number : undefined,
    seller_license_type: user.value.isSeller ? firstLicenseTypeObject?.displayType : undefined,
    seller_license_type_state: user.value.isSeller ? firstLicenseTypeObject?.state : undefined,
    state_location: activeCompany.value?.state,
    user_has_access_to_payments: Boolean(user.value?.platformAccess?.access?.paymentsPortal),
    marketplace_user_email: user.value.email,
  };

  return properties;
}

interface SegmentTrackOptions {
  /**
   * Defines whether application metadata will be sent to the Segment along with the payload.
   * Defaults to true.
   */
  appendMetaData?: boolean;
}

/**
 * @Deprecated Use `trackWithSegment` instead.
 *
 * Generic track handler that returns a function taking a typed payload.
 * Used to export functions specific to the event being tracked for ease.
 *
 * Including the SegmentEvent as a generic type arg helps TS display detailed
 * error messages resulting from invalid properties in inferred types.
 */
export const track =
  <TEvent extends SegmentEvent>(event: TEvent, options: SegmentTrackOptions = {}) =>
  (payload?: SegmentPayloadMap[keyof SegmentPayloadMap] | Record<string, unknown>) => {
    const analytics = getSegmentAnalytics();
    if (!analytics) {
      return false;
    }

    const { appendMetaData = true } = options;

    analytics.track(event, merge({}, appendMetaData ? getDefaultSegmentProperties() : undefined, payload));
    return true;
  };

/**
 * A wrapper around Segment's `analytics.page` method.
 * @returns {boolean} Whether the tracking was successful or not.
 */
export function trackPage(
  args: {
    category?: string;
    name?: string;
    properties?: object;
    options?: object;
    callback?: () => void;
  } = {},
) {
  const { category, name, properties, options, callback } = args;
  const analytics = getSegmentAnalytics();

  if (!analytics) {
    return false;
  } else {
    // https://segment.com/docs/sources/website/analytics.js/#page
    analytics.page(category, name, properties, options, callback);
    return true;
  }
}

/**
 * Reports Ad Click event data to Segment
 */
export const trackAdClick = track(SegmentEvent.AdClick);

/**
 * Reports Ad Impression event data to Segment
 */
export const trackAdImpression = track(SegmentEvent.AdImpression);

/**
 * Reports Brand Menu click event data to Segment
 */
export const trackBrandMenuClick = track(SegmentEvent.BrandMenuClick);

/**
 * Reports Brand Menu Search event data to Segment
 */
export const trackBrandMenuSearch = track(SegmentEvent.BrandMenuSearch);

/**
 * Reports BulkEditProductsClicked event data to Segment
 */
export const trackBulkEditProductsClicked = track(SegmentEvent.BulkEditProductsClicked);

/**
 * Reports BulkEditProductsSubmitted event data to Segment
 */
export const trackBulkEditProductsSubmitted = track(SegmentEvent.BulkEditProductsSubmitted);

/**
 * Reports Buyer Modal Product Added event data to Segment
 */
export const trackBuyerModalProductAdded = track(SegmentEvent.BuyerModalProductAdded);

/**
 * Reports Buyer Product Added to Cart event data to Segment
 */
export const trackBuyerProductAddedToCart = track(SegmentEvent.BuyerProductAddedToCart);

/**
 * Reports Buyer Product Edited event data to Segment
 */
export const trackBuyerProductEdited = track(SegmentEvent.BuyerProductEdited);

/**
 * Reports Buyer Product Removed event data to Segment
 */
export const trackBuyerProductRemoved = track(SegmentEvent.BuyerProductRemoved);

/**
 * Reports Buyer Product Searched event data to Segment
 */
export const trackBuyerProductSearched = track(SegmentEvent.BuyerProductSearched);

/**
 * Reports Company/License Changed event data to Segment
 */
export const trackCompanyLicenseChanged = track(SegmentEvent.CompanyLicenseChanged);

/**
 * Reports Custom Menu Export event data to Segment
 */
export const trackCustomMenuExport = track(SegmentEvent.CustomMenuExport);

/**
 * Reports Order Created event data to Segment
 */
export const trackOrderCreated = track(SegmentEvent.OrderCreated);

/**
 * Reports Order Paid Status Changed event data to Segment
 */
export const trackOrderPaidStatusChanged = track(SegmentEvent.OrderPaidStatusChanged);

/**
 * Reports Order Received event data to Segment
 */
export const trackOrderReceived = track(SegmentEvent.OrderReceived);

/**
 * Reports Orders Sent All Licenses Selected event data to Segment
 */
export const trackOrdersSentAllLicensesSelected = track(SegmentEvent.OrdersSentAllLicensesSelected);

/**
 * Reports Orders Submitted Collaborative event data to Segment
 */
export const trackOrderSubmittedCollaborative = track(SegmentEvent.OrderSubmittedCollaborative);

/**
 * Reports when a user clicks on an organically recommended product.
 */
export const trackProductClick = track(SegmentEvent.ProductClick);

/**
 * Reports when a user views an organically recommended product.
 */
export const trackProductImpression = track(SegmentEvent.ProductImpression);

/*
 * Reports Product Toggled event data to Segment
 */
export const trackProductToggled = track(SegmentEvent.ProductToggled);

/**
 * Reports Purchase List Item Added to Cart event data to Segment
 */
export const trackPurchaseListItemAddedToCart = track(SegmentEvent.PurchaseListItemAddedToCart);

/**
 * Reports Brand Send Message event data to Segment
 */
export const trackInAppMessageSent = track(SegmentEvent.InAppMessageSent);

/**
 * Reports Sent Sample Request event data to Segment
 */
export const trackSampleRequestSent = track(SegmentEvent.SampleRequestSent);

/**
 * Reports Vendor Menu - Click event data to Segment
 */
export const trackVendorMenuClick = track(SegmentEvent.VendorMenuClick);

/**
 * Reports the "View all Collaborative Orders" link being clicked on the buyer dashboard.
 */
export const trackViewCollabOrdersClicked = track(SegmentEvent.ViewCollabOrdersClicked);

/**
 * Reports when a user signs the terms and conditions
 */
export const trackAcceptTermsAndConditions = track(SegmentEvent.AcceptTermsAndConditions);

/**
 * Reports New Brand Click data to Segment
 */
export const trackNewBrandWidgetClick = track(SegmentEvent.NewBrandWidgetClick);

/**
 * Reports New Brand Impression data to Segment
 */
export const trackNewBrandWidgetImpression = track(SegmentEvent.NewBrandWidgetImpression);

/**
 * Reports New Brand Widget Displayed data to Segment
 */
export const trackNewBrandWidgetDisplayed = track(SegmentEvent.NewBrandWidgetDisplayed);

/**
 * Reports Product Promotion CTA Click data to Segment
 */
export const trackAdsManagerCtaClicked = track(SegmentEvent.AdsManagerCtaClicked);

/**
 * Reports Product Promotion Creation Form Completed data to Segment
 */
export const trackAdsManagerCreationFormCompleted = track(SegmentEvent.AdsManagerCreationFormCompleted);

/**
 * Reports Product Promotion Creation Form Step Completed data to Segment
 */
export const trackAdsManagerCreationFormStepCompleted = track(SegmentEvent.AdsManagerCreationFormStepCompleted);

/**
 * Reports Product Recommendation Carousel Impression data to Segment
 */
export const trackProductRecommendationCarouselImpression = track(SegmentEvent.ProductRecommendationCarouselImpression);

/**
 * Reports Product Spotlight Widget Click data to Segment
 */
export const trackProductSpotlightWidgetClick = track(SegmentEvent.ProductSpotlightWidgetClick);

/**
 * Reports Product Spotlight Widget Impression data to Segment
 */
export const trackProductSpotlightWidgetImpression = track(SegmentEvent.ProductSpotlightWidgetImpression);

/**
 * Reports when a user change step on Retailer registration page
 */
export const trackRetailerRegistrationSidebarClick = track(SegmentEvent.RetailerRegistrationSidebarClicked, {
  appendMetaData: false,
});

/**
 * Reports when a user submits Retailer Registration form
 */
export const trackRetailerRegistrationSubmission = track(SegmentEvent.RetailerRegistrationSubmission, {
  appendMetaData: false,
});

/**
 * Reports when a user lands on Retailer registration page.
 * It will return a tracking instance based on the type.
 */
export const trackRetailerRegistrationPageViewed = (
  type:
    | SegmentEvent.RetailerRegistrationLandingViewed
    | SegmentEvent.RetailerRegistrationAboutYouViewed
    | SegmentEvent.RetailerRegistrationYourBusinessViewed
    | SegmentEvent.RetailerRegistrationYourLicensesViewed
    | SegmentEvent.RetailerRegistrationReferralAndTermsViewed
    | SegmentEvent.RetailerRegistrationReviewInformationViewed,
) => track(type, { appendMetaData: false });

/**
 * Reports when a user clicks on Next button on Retailer registration page.
 */
export const trackRetailerRegistrationNavigationPrevClick = track(
  SegmentEvent.RetailerRegistrationNavigationPreviousClicked,
  { appendMetaData: false },
);

/**
 * Reports when a user clicks on Next button on Retailer registration page.
 */
export const trackRetailerRegistrationNavigationNextClick = track(
  SegmentEvent.RetailerRegistrationNavigationNextClicked,
  { appendMetaData: false },
);

/**
 * Reports when a user toggles a Notification Preference
 */
export const trackNotificationPreferenceClick = track(SegmentEvent.NotificationPreferenceClicked);

/**
 * Reports when a user clicks on Notification Preferences link on the User Profile menu.
 */
export const trackNotificationPreferencesClick = track(SegmentEvent.NotificationPreferencesClick);

/*
 * Reports Manual Order Created data to Segment
 */
export const trackManualOrderCreated = track(SegmentEvent.ManualOrderCreated);

/**
 * Reports a user sees the Category Management CTA
 */
export const trackCategoryManagementImpression = track(SegmentEvent.CatManImpression);

/**
 * Reports a user expresses interest in the Category Management program
 */
export const trackCategoryManagementInterest = track(SegmentEvent.CatManCurious);

/**
 * Reports when a PAR level insight is in the viewport
 */
export const trackCategoryManagementNativeInsightImpression = track(SegmentEvent.NativeInsightImpression);

/**
 * Reports when someone updates the quantity or notes field for a product that is attached to a native insight
 */
export const trackCategoryManagementNativeInsightCurious = track(SegmentEvent.NativeInsightCurious);

/**
 * Reports when someone adds a product to the cart that is attached to a native insight
 */
export const trackCategoryManagementNativeInsightInterested = track(SegmentEvent.NativeInsightInterested);

export const trackInventoryInsightsCategoryChanged = track(SegmentEvent.InventoryInsightsCategoryChanged);

/**
 * Reports when there are no products returned on the BrandMenu page
 */
export const trackEmptyBrandCatalogViewed = track(SegmentEvent.EmptyBrandCatalogViewed);

/**
 * Reports when Cancel Order Dialog is visible and user can click on the confirm button
 */
export const trackForwardOrderCancelled = track(SegmentEvent.ForwardOrderCancelled);

/**
 * Reports when orders are getting cancelled - triggered via Cancel Order Dialog
 */
export const trackOrderStatusSelectedCancelled = track(SegmentEvent.OrderStatusSelectedCancelled);

/**
 * Reports when product line add button is clicked
 */
export const trackingInventoryProductLineCreatedClicked = track(SegmentEvent.InventoryProductLineCreateClicked);

/**
 * Reports when a product line information is added when creating a new one
 */
export const trackingInventoryProductLineInfoAdded = track(SegmentEvent.InventoryProductLineInfoAdded);

/**
 * Reports when someone adds a new product line and hit the add button
 */
export const trackingInventoryProductLineInfoAdd = track(SegmentEvent.InventoryProductLineInfoAdd);

/**
 * Reports when someone adds a new product line but decides to cancel
 */
export const trackingInventoryProductLineInfoCancel = track(SegmentEvent.InventoryProductLineInfoCancel);

/**
 * Reports when a product line is removed
 */
export const trackingInventoryProductLineRemoved = track(SegmentEvent.InventoryProductLineRemoved);

/**
 * Reports when a user opens the new product template modal
 */
export const trackNewProductTemplateClicked = track(SegmentEvent.ProductTemplateAddClicked);

/**
 * Reports when a user picks a product template to edit
 */
export const trackPickProductTemplateClicked = track(SegmentEvent.ProductTemplatePickClicked);

/**
 * Reports when a user picks a product template to edit
 */
export const trackCancelProductTemplateClicked = track(SegmentEvent.ProductTemplateCancelClicked);

/**
 * Reports when a user creates a product template
 */
export const trackProductTemplateCreated = track(SegmentEvent.ProductTemplateCreated);

/**
 * Reports when a user updates a product template
 */
export const trackProductTemplateUpdated = track(SegmentEvent.ProductTemplateUpdated);

/**
 * Reports when a user sees a Brand, such as in the Recently Purchased Brands carousel
 */
export const trackBrandImpression = track(SegmentEvent.BrandImpression);

/**
 * Reports when a user sees a Vendor, such as in the Recently Purchased Vendors carousel
 */
export const trackVendorImpression = track(SegmentEvent.VendorImpression);

/**
 * Reports when a user clicks the filter, such as in the Recently Purchased Brands carousel
 */
export const trackFilterClicked = track(SegmentEvent.FilterClicked);

/**
 * Reports when a user clicks on a Vendor, such as in the Recently Purchased Vendors carousel
 */
export const trackVendorClicked = track(SegmentEvent.VendorClicked);

/**
 * Reports when a user clicks on a Brand, such as in the Recently Purchased Vendors carousel
 */
export const trackBrandClicked = track(SegmentEvent.BrandClicked);

/**
 * Reports Skyscraper Impression event data to Segment
 */
export const trackSkyscraperImpression = track(SegmentEvent.SkyscraperImpression);
